import React, { useMemo, useState, useRef, useEffect } from 'react';
import { TimberProject, ProjectTask, Milestone, DEPARTMENT_SWATCH_COLORS } from '../../types';
import './GanttChart.css';

interface GanttChartProps {
  projects: TimberProject[];
  tasks: ProjectTask[];
  milestones: Milestone[];
}

const DEPARTMENT_COLORS = DEPARTMENT_SWATCH_COLORS;

const DAY_WIDTH = 30;
const ROW_HEIGHT = 36;

export const GanttChart: React.FC<GanttChartProps> = ({
  projects,
  tasks,
  milestones,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  // Calculate date range
  const dateRange = useMemo(() => {
    const allDates: Date[] = [];
    
    projects.forEach(p => {
      allDates.push(new Date(p.startDate));
      allDates.push(new Date(p.targetEndDate));
    });
    
    tasks.forEach(t => {
      allDates.push(new Date(t.startDate));
      allDates.push(new Date(t.endDate));
    });
    
    milestones.forEach(m => {
      allDates.push(new Date(m.targetDate));
    });
    
    if (allDates.length === 0) {
      const now = new Date();
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: new Date(now.getFullYear(), now.getMonth() + 3, 0),
      };
    }
    
    const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
    
    // Add padding
    minDate.setDate(minDate.getDate() - 7);
    maxDate.setDate(maxDate.getDate() + 14);
    
    return { start: minDate, end: maxDate };
  }, [projects, tasks, milestones]);

  // Generate day columns
  const days = useMemo(() => {
    const result: Date[] = [];
    const current = new Date(dateRange.start);
    
    while (current <= dateRange.end) {
      result.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return result;
  }, [dateRange]);

  // Group days by month
  const months = useMemo(() => {
    const monthMap = new Map<string, { label: string; days: number; startIndex: number }>();
    
    days.forEach((day, index) => {
      const key = `${day.getFullYear()}-${day.getMonth()}`;
      if (!monthMap.has(key)) {
        monthMap.set(key, {
          label: day.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          days: 1,
          startIndex: index,
        });
      } else {
        monthMap.get(key)!.days++;
      }
    });
    
    return Array.from(monthMap.values());
  }, [days]);

  // Filter items
  const filteredProjects = selectedProjectId 
    ? projects.filter(p => p.id === selectedProjectId)
    : projects;
  
  const filteredTasks = selectedProjectId
    ? tasks.filter(t => t.projectId === selectedProjectId)
    : tasks;

  const filteredMilestones = selectedProjectId
    ? milestones.filter(m => m.projectId === selectedProjectId)
    : milestones;

  // Calculate position helpers
  const getDayOffset = (date: Date): number => {
    const diff = date.getTime() - dateRange.start.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24)) * DAY_WIDTH;
  };

  const getDuration = (start: Date, end: Date): number => {
    const diff = end.getTime() - start.getTime();
    return Math.max(1, Math.ceil(diff / (1000 * 60 * 60 * 24))) * DAY_WIDTH;
  };

  // Scroll to today on mount
  useEffect(() => {
    if (scrollRef.current) {
      const todayOffset = getDayOffset(new Date());
      scrollRef.current.scrollLeft = Math.max(0, todayOffset - 200);
    }
  }, []);

  // Today marker position
  const todayOffset = getDayOffset(new Date());
  const showTodayMarker = todayOffset >= 0 && todayOffset <= days.length * DAY_WIDTH;

  return (
    <div className="gantt-chart">
      {/* Chart Controls */}
      <div className="gantt-controls">
        <select
          value={selectedProjectId || ''}
          onChange={(e) => setSelectedProjectId(e.target.value || null)}
        >
          <option value="">All Projects</option>
          {projects.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <div className="legend">
          {Object.entries(DEPARTMENT_COLORS).map(([dept, color]) => (
            <div key={dept} className="legend-item">
              <span className="legend-color" style={{ backgroundColor: color }} />
              <span className="legend-label">{dept}</span>
            </div>
          ))}
          <div className="legend-item">
            <span className="legend-color milestone" />
            <span className="legend-label">Milestone</span>
          </div>
        </div>
      </div>

      {/* Chart Container */}
      <div className="gantt-container">
        {/* Row Labels */}
        <div className="gantt-labels">
          <div className="label-header">
            <span>Task / Project</span>
          </div>
          {filteredProjects.map(project => (
            <React.Fragment key={project.id}>
              <div className="label-row project">
                <span className="label-name">{project.name}</span>
              </div>
              {filteredTasks
                .filter(t => t.projectId === project.id)
                .map(task => (
                  <div key={task.id} className="label-row task">
                    <span className="label-name">{task.title}</span>
                    <span 
                      className="label-dept"
                      style={{ backgroundColor: DEPARTMENT_COLORS[task.department] }}
                    >
                      {task.department.charAt(0).toUpperCase()}
                    </span>
                  </div>
                ))}
              {filteredMilestones
                .filter(m => m.projectId === project.id)
                .map(milestone => (
                  <div key={milestone.id} className="label-row milestone">
                    <span className="label-name">◆ {milestone.title}</span>
                  </div>
                ))}
            </React.Fragment>
          ))}
        </div>

        {/* Timeline */}
        <div className="gantt-timeline" ref={scrollRef}>
          {/* Month Headers */}
          <div className="timeline-months">
            {months.map((month, index) => (
              <div 
                key={index}
                className="month-header"
                style={{ width: month.days * DAY_WIDTH }}
              >
                {month.label}
              </div>
            ))}
          </div>

          {/* Day Headers */}
          <div className="timeline-days">
            {days.map((day, index) => {
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;
              const isToday = day.toDateString() === new Date().toDateString();
              return (
                <div 
                  key={index}
                  className={`day-header ${isWeekend ? 'weekend' : ''} ${isToday ? 'today' : ''}`}
                  style={{ width: DAY_WIDTH }}
                >
                  {day.getDate()}
                </div>
              );
            })}
          </div>

          {/* Chart Rows */}
          <div className="timeline-rows">
            {/* Grid lines */}
            <div className="grid-lines">
              {days.map((day, index) => {
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                return (
                  <div 
                    key={index}
                    className={`grid-line ${isWeekend ? 'weekend' : ''}`}
                    style={{ left: index * DAY_WIDTH, width: DAY_WIDTH }}
                  />
                );
              })}
            </div>

            {/* Today marker */}
            {showTodayMarker && (
              <div 
                className="today-marker"
                style={{ left: todayOffset }}
              />
            )}

            {/* Bars */}
            {filteredProjects.map(project => {
              const projectTasks = filteredTasks.filter(t => t.projectId === project.id);
              const projectMilestones = filteredMilestones.filter(m => m.projectId === project.id);
              
              return (
                <React.Fragment key={project.id}>
                  {/* Project bar */}
                  <div className="gantt-row">
                    <div 
                      className="gantt-bar project-bar"
                      style={{
                        left: getDayOffset(new Date(project.startDate)),
                        width: getDuration(new Date(project.startDate), new Date(project.targetEndDate)),
                      }}
                    >
                      <span className="bar-label">{project.name}</span>
                    </div>
                  </div>

                  {/* Task bars */}
                  {projectTasks.map(task => (
                    <div key={task.id} className="gantt-row">
                      <div 
                        className={`gantt-bar task-bar ${task.status}`}
                        style={{
                          left: getDayOffset(new Date(task.startDate)),
                          width: getDuration(new Date(task.startDate), new Date(task.endDate)),
                          backgroundColor: DEPARTMENT_COLORS[task.department],
                        }}
                      >
                        {task.progress > 0 && (
                          <div 
                            className="bar-progress"
                            style={{ width: `${task.progress}%` }}
                          />
                        )}
                        <span className="bar-label">{task.title}</span>
                      </div>
                    </div>
                  ))}

                  {/* Milestone markers */}
                  {projectMilestones.map(milestone => (
                    <div key={milestone.id} className="gantt-row">
                      <div 
                        className={`gantt-milestone ${milestone.status}`}
                        style={{
                          left: getDayOffset(new Date(milestone.targetDate)),
                        }}
                        title={`${milestone.title}: ${new Date(milestone.targetDate).toLocaleDateString()}`}
                      >
                        ◆
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GanttChart;
